import { ChevronRight } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { spendRows } from './expenseDashboardLogic'
import { CategoryLabel, MONEY_SERIES, useMoney } from './moneyUi'
import { useExpenseCategoryDefs } from './use-expense-category-defs'

type Props = {
  categories?: { category: string; total: number; count?: number; stock_total?: number }[]
  /** Opens the ledger filtered to the category, for the period on screen. */
  onDrill: (slug: string) => void
  emptyText?: string
  /** Show the entry count beside the amount (the ledger summary has it; the P&L breakdown does not). */
  showCount?: boolean
}

/**
 * "Spend by category" — one row per category, biggest first, with a bar for
 * scale, the share of the period's spend, and a click that opens the ledger
 * filtered to exactly those rows (F-FE-N129).
 *
 * F-FE-N164: every bar is the one operating-expense colour. The rows are
 * already ranked and labelled, so painting each in its category's colour
 * re-encoded the name and made five bars look like five different things.
 */
export function CategorySpendList({ categories, onDrill, emptyText = 'No expenses in this period', showCount = false }: Props) {
  const money = useMoney()
  const { data: defs = [] } = useExpenseCategoryDefs()
  const rows = spendRows(
    categories ? { categories: categories.map(c => ({ category: c.category, total: c.total, count: c.count ?? 0, stock_total: c.stock_total ?? 0 })) } : undefined,
    defs,
  )

  if (rows.length === 0) {
    return <p className="py-6 text-center text-sm text-muted-foreground">{emptyText}</p>
  }

  const total = rows.reduce((s, r) => s + r.total, 0)

  return (
    <div>
      <ul className="-mx-2">
        {rows.map(r => (
          <li key={r.slug}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => onDrill(r.slug)}
                  className="group grid w-full grid-cols-[minmax(0,1fr)_auto_1rem] items-center gap-x-3 gap-y-1.5 rounded-md px-2 py-2.5 text-left transition-colors hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <span className="flex min-w-0 items-center gap-2 text-sm">
                    <CategoryLabel label={r.label} color={r.color} />
                    {r.isStock && <span className="shrink-0 text-xs text-muted-foreground">stock</span>}
                    {r.isCredit && <span className="shrink-0 text-xs text-success-fg">credit</span>}
                    {showCount && r.count > 0 && (
                      <span className="shrink-0 text-xs text-muted-foreground">
                        {r.count} {r.count === 1 ? 'entry' : 'entries'}
                      </span>
                    )}
                  </span>
                  <span className="text-right text-sm font-medium tabular-nums">
                    {money.whole(r.total)}
                    {!r.isCredit && (
                      <span className="ml-2 inline-block w-11 text-right text-xs font-normal text-muted-foreground">{r.sharePct.toFixed(1)}%</span>
                    )}
                  </span>
                  <ChevronRight
                    className="row-span-2 h-4 w-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
                    aria-hidden
                  />
                  {!r.isCredit && (
                    <span className="col-span-2 h-1.5 overflow-hidden rounded-full bg-muted" aria-hidden>
                      <span
                        className="block h-full rounded-full"
                        style={{ width: `max(2px, ${r.barPct}%)`, background: MONEY_SERIES.opex }}
                      />
                    </span>
                  )}
                </button>
              </TooltipTrigger>
              <TooltipContent side="left">
                {r.isCredit
                  ? `${r.label}: ${money.full(r.total)} credited back. Open these entries in the ledger.`
                  : `${r.label}: ${money.full(r.total)}, ${r.sharePct.toFixed(1)}% of the period’s spend. Open these entries in the ledger.`}
              </TooltipContent>
            </Tooltip>
          </li>
        ))}
      </ul>
      <div className="mt-2 flex items-baseline justify-between border-t pt-3 text-sm">
        <span className="text-muted-foreground">Total</span>
        <span className="font-medium tabular-nums">{money.whole(total)}</span>
      </div>
    </div>
  )
}
